<template>
    <li class="a-accordion-item" :class="{ active: isActive }">
        <component :is="titleTag" class="title" @click="onClick">
            <template v-if="title">
                <a-glyph class="icon" name="icon-pointer-right" />
                {{ title }}
            </template>
            <template v-else-if="$slots.title">
                <slot name="title" />
            </template>
        </component>

        <div v-if="$slots.default" ref="content" class="content">
            <div ref="inner" class="content-wrapper">
                <slot />
            </div>
        </div>
    </li>
</template>

<script setup lang="ts">
import { ref, inject, onMounted, onBeforeUnmount } from 'vue';
import AGlyph from '@core/components/glyph/glyph.vue';

defineOptions({ name: 'AAccordionItem' });

const props = defineProps({
    title: { type: String, default: '' },
    titleTag: { type: String, default: 'div' },
    active: Boolean,
});

const isActive = ref(false);
const content = ref<HTMLElement | null>(null);

const parent: any = inject('accordionParent');
const register = inject('registerAccordionItem') as (item: any) => void;

let observer: MutationObserver | null = null;

const changeHeight = () => {
    const el = content.value;
    if (!el) return;
    el.style.height = `${el.scrollHeight}px`;
};

const changeHeightIfActive = () => {
    if (isActive.value) changeHeight();
};

const onClick = () => {
    const willOpen = !isActive.value;
    parent?.handleAllActives?.();
    isActive.value = willOpen;
    changeHeightIfActive();
    parent?.emitActiveChanged?.(isActive.value);
    parent?.emitChangeEvent?.();
};

onMounted(() => {
    isActive.value = props.active;

    register?.({
        isActive: () => isActive.value,
        removeActive: () => (isActive.value = false),
    });

    changeHeightIfActive();
    window.addEventListener('resize', changeHeightIfActive);

    // MutationObserver for dynamic inner content
    if (content.value) {
        observer = new MutationObserver((mutations) => {
            if (mutations.some((x) => x.type === 'childList')) {
                changeHeightIfActive();
            }
        });
        observer.observe(content.value, {
            childList: true,
            subtree: true,
        });
    }
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', changeHeightIfActive);
    observer?.disconnect();
});
</script>

<style lang="postcss" scoped>
.a-accordion-item {
  border-top: var(--av-border);

  &:not(.active) {
    .content {
      height: 0 !important;
    }
  }

  .title {
    cursor: pointer;
    display: flex;
    color: var(--av-nav-primary);
    transition: color ease-out 0.3s;
    padding: 16px 40px 16px 16px;

    &:hover {
      color: var(--av-nav-secondary);

      :deep(.icon) {
        fill: var(--av-nav-secondary);
      }
    }
  }

  .content {
    overflow: hidden;
    max-height: max-content;
    font-size: 14px;
    line-height: 20px;
    transition: height 0.2s ease-in-out;
  }

  .content-wrapper {
    padding: 8px 40px 16px;
  }

  :deep(.icon) {
    margin-top: 4px;
    transition: fill ease-out 0.3s, transform 0.25s ease-out;
    flex-shrink: 0;
    fill: var(--av-brand-secondary);
    margin-inline-end: 8px;
  }

  &.active :deep(.icon) {
    transform: rotate(90deg);
  }
}

[dir="rtl"] {
  .a-accordion-item :deep(.icon) {
    transform: rotate(180deg);
  }
}
</style>
